import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Table,
  Button,
  Space,
  Modal,
  Tag,
  Input,
  Select,
  Row,
  Col,
  Tooltip
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  ReloadOutlined,
  AppstoreOutlined,
  CheckOutlined,
  CloseOutlined,
  LockOutlined,
} from '@ant-design/icons';
import {
  fetchProductCategoriesRequest,
  setProductCategoryEditing,
  clearProductCategoryEditing
} from '../../../redux/actions/productCategoryActions';
import { hasPermission } from '../../../utils/permissions';
import ProductCategoryForm from './ProductCategoryForm';
import './ProductCategory.css';

const { Search } = Input;
const { Option } = Select;

const ProductCategory = () => {
  const dispatch = useDispatch();
  const { categories, loading, totalCount } = useSelector(state => state.productCategories);
  const permissions = useSelector(state => state.auth.permissions || []);
  
  // Check permissions
  const canViewGrid = hasPermission(permissions, 'product_category/gridView');
  const canCreate = hasPermission(permissions, 'product_category/create');
  const canEdit = hasPermission(permissions, 'product_category/edit');
  
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [filters, setFilters] = useState({
    is_active: undefined,
    category_name: ''
  });

  useEffect(() => {
    if (canViewGrid) {
      dispatch(fetchProductCategoriesRequest(filters));
    }
  }, [dispatch, filters, canViewGrid]);

  const handleAddNew = () => {
    dispatch(clearProductCategoryEditing());
    setIsModalVisible(true);
  };

  const handleEdit = (record) => {
    dispatch(setProductCategoryEditing(record));
    setIsModalVisible(true);
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
    dispatch(clearProductCategoryEditing());
  };

  const handleRefresh = () => {
    dispatch(fetchProductCategoriesRequest(filters));
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      is_active: undefined,
      category_name: ''
    });
  };

  // If user doesn't have permission to view grid, show message
  if (!canViewGrid) {
    return (
      <div className="empty-state">
        <div className="empty-state-icon"><LockOutlined /></div>
        <div className="empty-state-text">
          You don't have permission to view product categories.
        </div>
      </div>
    );
  }

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: 'Category Name',
      dataIndex: 'category_name',
      key: 'category_name',
      ellipsis: {
        showTitle: false,
      },
      render: (category_name) => (
        <Tooltip placement="topLeft" title={category_name}>
          <strong>{category_name}</strong>
        </Tooltip>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 120,
      render: (is_active) => (
        <Tag color={is_active ? 'success' : 'error'}>
          {is_active ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Created Date',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (created_at) => {
        if (!created_at) return '-';
        return new Date(created_at).toLocaleDateString('en-IN', {
          year: 'numeric',
          month: 'short',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        });
      },
    },
    {
      title: 'Updated Date',
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 180,
      render: (updated_at) => {
        if (!updated_at) return '-';
        return new Date(updated_at).toLocaleDateString('en-IN', {
          year: 'numeric',
          month: 'short',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        });
      },
    },
    ...(canEdit ? [{
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Edit">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
              size="small"
            />
          </Tooltip>
        </Space>
      ),
    }] : []),
  ];

  return (
    <div style={{ padding: '1rem' }}>
      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon"><AppstoreOutlined /></div>
          <div className="stat-content">
            <div className="stat-value">{(categories || []).length}</div>
            <div className="stat-label">Total Categories</div>
          </div>
        </div>
        <div className="stat-card" style={{ borderLeftColor: '#28a745' }}>
          <div className="stat-icon" style={{ background: '#d4edda', color: '#155724' }}><CheckOutlined /></div>
          <div className="stat-content">
            <div className="stat-value">{(categories || []).filter(c => c.is_active).length}</div>
            <div className="stat-label">Active</div>
          </div>
        </div>
        <div className="stat-card" style={{ borderLeftColor: '#dc3545' }}>
          <div className="stat-icon" style={{ background: '#f8d7da', color: '#721c24' }}><CloseOutlined /></div>
          <div className="stat-content">
            <div className="stat-value">{(categories || []).filter(c => !c.is_active).length}</div>
            <div className="stat-label">Inactive</div>
          </div>
        </div>
      </div>

      {/* Table Container */}
      <div className="modern-table-container" style={{ marginTop: '1.5rem' }}>
        <div className="table-header">
          <h2 className="table-title">Product Categories</h2>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button className="btn btn-secondary" onClick={handleRefresh}>
              <ReloadOutlined /> Refresh
            </button>
            {canCreate && (
              <button className="btn btn-primary" onClick={handleAddNew}>
                <PlusOutlined /> Add New Category
              </button>
            )}
          </div>
        </div>

        {/* Filters */}
        <div style={{ padding: '1rem', background: '#f8f9fa', borderRadius: '8px', marginBottom: '1rem' }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8}>
            <Search
              placeholder="Search by category name"
              value={filters.category_name}
              onChange={(e) => handleFilterChange('category_name', e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder="Status"
              value={filters.is_active}
              onChange={(value) => handleFilterChange('is_active', value)}
              allowClear
              style={{ width: '100%' }}
            >
              <Option value={true}>Active</Option>
              <Option value={false}>Inactive</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Space>
              <Button onClick={clearFilters}>
                Clear Filters
              </Button>
            </Space>
          </Col>
        </Row>
        </div>

        <Table
          columns={columns}
          dataSource={categories}
          rowKey="id"
          loading={loading}
          pagination={{
            total: totalCount,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} categories`,
          }}
          scroll={{ x: 800 }}
          locale={{
            emptyText: (
              <div className="empty-state">
                <div className="empty-state-icon"><AppstoreOutlined /></div>
                <div className="empty-state-text">No product categories found</div>
                {canCreate && (
                  <button className="btn btn-primary" onClick={handleAddNew}>
                    Add Your First Category
                  </button>
                )}
              </div>
            ),
          }}
        />
      </div>

      <Modal
        open={isModalVisible}
        onCancel={handleModalClose}
        footer={null}
        width={600}
        destroyOnClose
        centered
      >
        <ProductCategoryForm onCancel={handleModalClose} />
      </Modal>
    </div>
  );
};

export default ProductCategory;